/**
 * @file     ListingCardsBlock.tsx
 * @purpose  Renders a content-engine listing_cards block as a responsive grid of real PropertyCards with a section heading and a "See all" link.
 */
'use client';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { PropertyCard } from '@/features/properties/components/PropertyCard/PropertyCard';
import type { MlsListingCardData } from '@/features/properties/services/cardAdapters';
import type { ContentBlock, ListingCardRef } from '@/services/content-engine/types';

type ListingCardsBlockProps = Extract<ContentBlock, { t: 'listing_cards' }>;

/** A frozen ListingCardRef is already card-shaped; this fills the optional
 *  fields PropertyCard reads so the editorial card renders the dense variant. */
const toCardData = (ref: ListingCardRef): MlsListingCardData => ({
  listingKey: ref.listingKey,
  listingSlug: ref.listingSlug,
  listPrice: ref.listPrice,
  bedroomsTotal: ref.bedroomsTotal,
  bathroomsTotalInteger: ref.bathroomsTotalInteger,
  city: ref.city,
  streetName: ref.streetName,
  streetNumber: ref.streetNumber,
  streetSuffix: ref.streetSuffix,
  unitNumber: ref.unitNumber,
  propertyType: ref.propertyType,
  latitude: ref.latitude,
  longitude: ref.longitude,
  modificationTimestamp: ref.modificationTimestamp,
  primaryMediaKey: ref.primaryMediaKey,
  primaryMediaUrl: ref.primaryMediaUrl,
  livingArea: ref.livingArea,
  yearBuilt: ref.yearBuilt,
  photosCount: ref.photosCount,
});

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h13M13 6l6 6-6 6" />
  </svg>
);

export const ListingCardsBlock = ({ caption, href, listings }: ListingCardsBlockProps) => {
  const { theme } = useAppTheme();
  const { t } = useTranslation();

  // A malformed frozen row must not blank the article.
  const usable = listings.filter((l) => Boolean(l?.listingKey) && Boolean(l?.propertyType));

  if (usable.length === 0) return null;

  return (
    <Box component="section">
      <Stack
        direction="row"
        sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', mb: '16px' }}
      >
        {caption ? (
          <Typography
            component="h2"
            sx={{
              fontFamily: theme.typography.fontFamilyHeading,
              fontSize: theme.typography.bodyLg,
              fontWeight: 600,
              color: theme.semanticColors.textPrimary,
            }}
          >
            {caption}
          </Typography>
        ) : (
          <span />
        )}

        {href ? (
          <Box
            component={Link}
            href={href}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              minHeight: 44,
              cursor: 'pointer',
              fontSize: theme.typography.bodySm,
              fontWeight: 600,
              color: theme.semanticColors.interactivePrimary,
              textDecoration: 'none',
              transition: 'opacity 180ms ease',
              '&:hover': { opacity: 0.75 },
              '&:focus-visible': {
                outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`,
                outlineOffset: '3px',
                borderRadius: `${theme.layout.radius.sm}px`,
              },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            {t('blog.blocks.listings.seeAll', { defaultValue: 'See all listings' })}
            <ArrowIcon />
          </Box>
        ) : null}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: '20px',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {usable.map((listing) => (
          <PropertyCard key={listing.listingKey} source="mls" listing={toCardData(listing)} hideLikeButton presentation="bare" />
        ))}
      </Box>
    </Box>
  );
};
